import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Chip,
  tableCellClasses,
  CircularProgress,
  Fab,
  Fade,
  Autocomplete,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchDayBookReport, downloadDayBookReportExcel, downloadDayBookReportPdf, fetchAccountLedgers } from '../../store/reportStore/reportThunk';
import ReusableReportHeader from '../../components/Reports/ReusableReportHeader';
import Filters from '../../components/Filters/Filters';
import { toast } from 'react-toastify';
import styled_ from "@emotion/styled";
import { saveAs } from 'file-saver';
import { Utils } from '../../utils/common-utils';
import moment from 'moment';

const StyledTableCell = styled_(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#eaecf0',
    color: '#000',
    border: '0.75px solid #d9d4d4',
    margin: 0,
    padding: 5,
    fontSize: '9pt',
    paddingLeft: '15px !important',
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    verticalAlign: 'super',
    fontSize: '9pt',
    fontWeight: 'normal',
    color: '#292d35',
    padding: 5,
    border: '0.75px solid #d9d4d4',
    paddingLeft: '15px !important',
    margin: 0,
    backgroundColor: '#fff',
  },
}));

const StyledTableRow = styled_(TableRow)(({ theme }) => ({
  '&:last-child td, &:last-child th': {},
}));

interface DayBookEntry {
  journalEntryId: string;
  date: string;
  dateSort: string;
  ledgerId: string;
  ledgerName: string;
  ledgerCode: string;
  groupCategory: string;
  primaryGroup: string;
  subGroup: string;
  particulars: string;
  partyName: string;
  voucherNumber: string;
  voucherType: string;
  debit: string;
  credit: string;
  runningBalance: string;
  balanceType: 'Dr' | 'Cr';
  createdBy: string;
  createdAt: string;
}

interface DayBookData {
  entries: DayBookEntry[];
  summary: {
    totalDebit: string;
    totalCredit: string;
    closingBalance: string;
    closingBalanceType: 'Dr' | 'Cr';
    totalEntries: number;
  };
  pagination: {
    currentPage: number;
    totalPages: number;
    totalRecords: number;
    limit: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

// Helper function to format voucher type labels - removes underscores
const formatVoucherTypeLabel = (voucherType: string): string => {
  if (!voucherType) return '';

  // Replace underscores with spaces (e.g., "EXPENSE_PAYMENT" -> "EXPENSE PAYMENT")
  // Then apply title case (e.g., "EXPENSE PAYMENT" -> "Expense Payment")
  return voucherType
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const DayBookReportPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { selectedAccount } = useAppSelector((state) => state.auth);
  const { accountLedgers } = useAppSelector((state) => state.reports);
  const accountId = selectedAccount?._id;

  const [filters, setFilters] = useState('');
  const [filterData, setFilterData] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [dayBookData, setDayBookData] = useState<DayBookData | null>(null);
  const [allEntries, setAllEntries] = useState<DayBookEntry[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLedgers, setSelectedLedgers] = useState<any[]>([]);
  const [viewMode, setViewMode] = useState<'detailed' | 'simple'>('detailed');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [rowsPerPage] = useState(50);

  // Scroll button states
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('down');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollTop = useRef(0);

  // Fetch Day Book Report (initial load or filter change)
  const fetchReport = (pageNum: number = 1, limit: number = rowsPerPage, search: string = searchTerm, append: boolean = false, ledgers: any[] = selectedLedgers, mode: 'detailed' | 'simple' = viewMode) => {
    if (!accountId || !filters) return;

    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }

    let queryParams = `${filters}&page=${pageNum}&limit=${limit}&viewMode=${mode}`;

    // Add search filter (searches in Voucher No. and Voucher Type)
    if (search.trim()) {
      queryParams += `&search=${encodeURIComponent(search.trim())}`;
    }

    // Add ledger filter
    if (ledgers.length > 0) {
      const ledgerIds = ledgers.map(l => l._id).join(',');
      queryParams += `&ledgerIds=${ledgerIds}`;
    }

    dispatch(fetchDayBookReport({ accountId, params: queryParams }))
      .unwrap()
      .then((response) => {
        if (response.success) {
          setDayBookData(response.data);

          if (append) {
            // Append new entries to existing ones
            setAllEntries(prev => [...prev, ...response.data.entries]);
          } else {
            // Replace entries (initial load or filter change)
            setAllEntries(response.data.entries);
          }

          // Check if there are more pages
          setHasMore(response.data.pagination.hasNextPage);
        } else {
          toast.error('Failed to fetch Day Book report');
        }
      })
      .catch((error) => {
        toast.error(error.message || 'Failed to fetch Day Book report');
      })
      .finally(() => {
        setIsLoading(false);
        setIsLoadingMore(false);
      });
  };

  // Fetch ledgers list when filters change
  useEffect(() => {
    if (accountId && filters) {
      dispatch(fetchAccountLedgers({ accountId, params: filters }));
    }
  }, [accountId, filters, dispatch]);

  // Fetch report when filters change (reset to page 1)
  useEffect(() => {
    if (filters) {
      setPage(1);
      setAllEntries([]);
      setSelectedLedgers([]); // Reset selected ledgers when filters change
      fetchReport(1, rowsPerPage, searchTerm, false, []);
    }
  }, [filters]);

  const filterOnChangeHandler = (data: any) => {
    setFilters(data);
  };

  const filterdOnChangeHandler = (data: any) => {
    setFilterData(data);
  };

  const handleLedgerSelectionChange = (event: any, value: any[]) => {
    setSelectedLedgers(value);
    // Automatically trigger report fetch when ledgers change
    if (accountId && filters) {
      setPage(1);
      setAllEntries([]);
      fetchReport(1, rowsPerPage, searchTerm, false, value, viewMode);
    }
  };

  const handleViewModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMode = event.target.checked ? 'simple' : 'detailed';
    setViewMode(newMode);
    // Trigger report fetch with new view mode
    if (accountId && filters) {
      setPage(1);
      setAllEntries([]);
      fetchReport(1, rowsPerPage, searchTerm, false, selectedLedgers, newMode);
    }
  };

  // Debounced search - triggers API call 500ms after user stops typing
  useEffect(() => {
    if (!filters) return;

    const timeoutId = setTimeout(() => {
      setPage(1);
      setAllEntries([]);
      fetchReport(1, rowsPerPage, searchTerm, false);
    }, 500);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Load more data when user scrolls near bottom
  const loadMore = () => {
    if (isLoadingMore || !hasMore) return;

    const nextPage = page + 1;
    setPage(nextPage);
    fetchReport(nextPage, rowsPerPage, searchTerm, true);
  };

  const handleDownloadExcel = () => {
    if (!accountId || !filters) {
      toast.warn('Please select date range first');
      return;
    }

    let queryParams = `${filters}&isDownloadExcel=true`;

    if (searchTerm.trim()) {
      queryParams += `&search=${encodeURIComponent(searchTerm.trim())}`;
    }

    dispatch(downloadDayBookReportExcel({ accountId, params: queryParams }))
      .unwrap()
      .then((blob) => {
        const fileName = `Day_Book_Report_${new Date().getTime()}.xlsx`;
        saveAs(blob, fileName);
        toast.success('Excel file downloaded successfully');
      })
      .catch((error) => {
        toast.error(error.message || 'Failed to download Excel file');
      });
  };

  const handleDownloadPdf = () => {
    if (!accountId || !filters) {
      toast.warn('Please select date range first');
      return;
    }

    let queryParams = `${filters}&isDownloadPdf=true`;

    if (searchTerm.trim()) {
      queryParams += `&search=${encodeURIComponent(searchTerm.trim())}`;
    }

    dispatch(downloadDayBookReportPdf({ accountId, params: queryParams }))
      .unwrap()
      .then((blob) => {
        const fileName = `Day_Book_Report_${new Date().getTime()}.pdf`;
        saveAs(blob, fileName);
        toast.success('PDF file downloaded successfully');
      })
      .catch((error) => {
        toast.error(error.message || 'Failed to download PDF file');
      });
  };

  // Handle scroll event
  const handleScroll = () => {
    if (!scrollContainerRef.current) return;

    const currentScrollTop = scrollContainerRef.current.scrollTop;
    const scrollHeight = scrollContainerRef.current.scrollHeight;
    const clientHeight = scrollContainerRef.current.clientHeight;

    // Determine scroll direction
    if (currentScrollTop > lastScrollTop.current) {
      setScrollDirection('down');
    } else if (currentScrollTop < lastScrollTop.current) {
      setScrollDirection('up');
    }

    lastScrollTop.current = currentScrollTop;

    // Show button when scrolling
    if (currentScrollTop > 100 || currentScrollTop < scrollHeight - clientHeight - 100) {
      setShowScrollButton(true);

      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Hide button after 2 seconds of no scrolling
      scrollTimeoutRef.current = setTimeout(() => {
        setShowScrollButton(false);
      }, 2000);
    }

    // Infinite scroll: Load more when user scrolls near bottom (within 200px)
    const distanceFromBottom = scrollHeight - clientHeight - currentScrollTop;
    if (distanceFromBottom < 200 && hasMore && !isLoadingMore && !isLoading) {
      loadMore();
    }
  };

  // Handle scroll button click
  const handleScrollButtonClick = () => {
    if (!scrollContainerRef.current) return;

    const scrollHeight = scrollContainerRef.current.scrollHeight;
    const clientHeight = scrollContainerRef.current.clientHeight;

    if (scrollDirection === 'down') {
      // Scroll to bottom
      scrollContainerRef.current.scrollTo({
        top: scrollHeight,
        behavior: 'smooth'
      });
    } else {
      // Scroll to top
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box sx={{ width: 'calc(100% - 7px)', height: 'calc(100vh - 70px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', mb: 2, position: 'relative' }} className={'c-card2'}>
          {/* Scrollable content area */}
          <Box
            ref={scrollContainerRef}
            onScroll={handleScroll}
            sx={{
              flex: 1,
              overflow: 'auto',
              minHeight: 0,
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none'
            }}>
            {/* Report Header */}
            <ReusableReportHeader
              reportType="DAY_BOOK"
              title="Day Book"
              subtitle={(filterData && filterData.periodString) ? filterData.periodString : 'Journal Entry Ledgers Statement'}
              recordCount={dayBookData?.summary?.totalEntries || 0}
              isLoading={isLoading}
              onDownloadPDF={handleDownloadPdf}
              onDownloadExcel={handleDownloadExcel}
              showPrint={false}
              showShare={false}
            />

            {/* Filters and Summary Section */}
            <Grid container px={2} py={1.5} alignItems="flex-start">
              {/* Left side - All Filters */}
              <Grid item xs={12}>
                {/* Date Filter and Search */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                  <Box>
                    <Filters
                      type="report"
                      filterHandler={filterOnChangeHandler}
                      selectedFilters={filterdOnChangeHandler}
                      hideFilter={true}
                    />
                  </Box>
                  <TextField
                    className='c-text-field-3'
                    variant="standard"
                    size="small"
                    placeholder="Search Voucher Type, Voucher No."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ minWidth: '280px' }}
                    InputProps={{
                      disableUnderline: true,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box sx={{ width: '200px' }}>
                    <Autocomplete
                      multiple
                      id="ledger-select"
                      options={accountLedgers || []}
                      getOptionLabel={(option: any) => `${option.name} (${option.code})`}
                      value={selectedLedgers}
                      onChange={handleLedgerSelectionChange}
                      disableCloseOnSelect
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          className='c-text-field-3'
                          variant="standard"
                          size="small"
                          placeholder="Select ledgers to filter..."
                          InputProps={{ ...params.InputProps, disableUnderline: true }}
                          sx={{
                            '& .MuiInputBase-root': {
                              fontSize: '0.875rem',
                              paddingTop: '2px !important',
                              display: 'flex',
                              alignItems: 'center'
                            },
                            '& .MuiInputBase-input': {
                              paddingTop: '0 !important',
                              paddingBottom: '0 !important'
                            },
                            '& .MuiAutocomplete-endAdornment': {
                              top: '50%',
                              transform: 'translateY(-50%)'
                            }
                          }}
                        />
                      )}
                      renderOption={(props, option: any) => (
                        <li {...props}>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>{option.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.code} • {option.groupCategory}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      renderTags={() => null}
                      sx={{ width: '100%' }}
                    />
                  </Box>

                  {/* View Mode Switch */}
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={viewMode === 'simple'}
                          onChange={handleViewModeChange}
                          size="small"
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#555' }}>
                          Simple View
                        </Typography>
                      }
                    />
                  </Box>

                </Box>

              </Grid>
            </Grid>

            {/* Selected Ledgers Display - Below filters, above summary */}
            {selectedLedgers.length > 0 && (
              <Box sx={{ px: 2, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#666' }}>
                  Filtered Ledgers:
                </Typography>
                {selectedLedgers.map((ledger: any) => (
                  <Chip
                    key={ledger._id}
                    label={`${ledger.name} (${ledger.code})`}
                    size="small"
                    onDelete={() => {
                      const newLedgers = selectedLedgers.filter(l => l._id !== ledger._id);
                      handleLedgerSelectionChange(null, newLedgers);
                    }}
                    sx={{
                      fontSize: '0.75rem',
                      height: '24px',
                      backgroundColor: '#e3f2fd',
                      '& .MuiChip-deleteIcon': {
                        fontSize: '16px'
                      }
                    }}
                  />
                ))}
              </Box>
            )}

            {!isLoading ? (
              <>
                {/* Summary Row Above Table */}
                {dayBookData?.summary && (
                  <Box sx={{ px: 2, py: 1.5, backgroundColor: '#f8fafc', borderBottom: '2px solid #cbd5e1' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                          <strong>Total Entries:</strong> {dayBookData.summary.totalEntries}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#d32f2f' }}>
                          <strong>Total Debit:</strong> {Utils.formatNumber(parseFloat(dayBookData.summary.totalDebit || '0').toFixed(2))}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#2e7d32' }}>
                          <strong>Total Credit:</strong> {Utils.formatNumber(parseFloat(dayBookData.summary.totalCredit || '0').toFixed(2))}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500, color: dayBookData.summary.closingBalanceType === 'Dr' ? '#2e7d32' : '#d32f2f' }}>
                          <strong>Closing Balance:</strong> {Utils.formatNumber(parseFloat(dayBookData.summary.closingBalance || '0').toFixed(2))} {dayBookData.summary.closingBalanceType}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                <Table sx={{ minWidth: 700, tableLayout: 'fixed', width: '100%' }} size='small' aria-label="customized table">
                  <colgroup>
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '22%' }} />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '12%' }} />
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Date</StyledTableCell>
                      <StyledTableCell>Party & Particulars</StyledTableCell>
                      <StyledTableCell>Voucher Type</StyledTableCell>
                      <StyledTableCell>Voucher No.</StyledTableCell>
                      <StyledTableCell>Ledger Name</StyledTableCell>
                      <StyledTableCell style={{ textAlign: 'right' }}>Debit</StyledTableCell>
                      <StyledTableCell style={{ textAlign: 'right' }}>Credit</StyledTableCell>
                      <StyledTableCell style={{ textAlign: 'right' }}>Running Balance</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(allEntries && allEntries.length > 0) ? allEntries.map((entry, index) => (
                      <StyledTableRow key={`${entry.journalEntryId}-${entry.ledgerId}-${index}`}>
                        <StyledTableCell style={{ verticalAlign: 'middle' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.4' }}>
                            <Typography variant="body2" fontWeight={500}>
                              {entry.date}
                            </Typography>
                            {entry.createdAt && entry.createdAt !== '-' && (
                              <Tooltip 
                                title={`Created by ${entry.createdBy || 'System'} on ${moment(entry.createdAt).format('DD/MM/YYYY, hh:mm A')}`}
                                arrow
                                placement="right"
                              >
                                <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      color: '#6b7280', 
                                      fontSize: '7pt',
                                      cursor: 'help',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      maxWidth: '100px'
                                    }}
                                  >
                                    By: {entry.createdBy || 'System'}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            )}
                          </div>
                        </StyledTableCell>
                        <StyledTableCell style={{ verticalAlign: 'middle' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.4' }}>
                            {entry.partyName && (
                              <span style={{ fontWeight: '600', fontSize: '9pt', color: '#1e40af' }}>
                                {entry.partyName}
                              </span>
                            )}
                            {entry.particulars && (
                              <span style={{ fontSize: '9pt', color: '#292d35' }}>
                                {entry.particulars}
                              </span>
                            )}
                          </div>
                        </StyledTableCell>
                        <StyledTableCell style={{ verticalAlign: 'middle' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.5' }}>
                            <Chip
                              label={formatVoucherTypeLabel(entry.voucherType)}
                              size="small"
                              variant="outlined"
                              style={{
                                fontSize: '9pt',
                                height: '22px',
                                // fontWeight: 'bold',
                                borderColor:
                                  entry.voucherType === 'Sales Invoice' ? '#3b82f6' :
                                    entry.voucherType === 'Purchase Invoice' ? '#dc2626' :
                                      entry.voucherType === 'Receipt' ? '#059669' :
                                        entry.voucherType === 'Payment' ? '#7c2d12' :
                                          entry.voucherType === 'Tax Settlement' ? '#7c3aed' :
                                            entry.voucherType === 'Opening Balance' ? '#ea580c' :
                                              entry.voucherType === 'Journal Entry' ? '#0891b2' :
                                                entry.voucherType.includes('EXPENSE') || entry.voucherType.includes('SALARY') || entry.voucherType.includes('Daily Wage') ? '#f59e0b' :
                                                  '#6b7280',
                                // color:
                                //   entry.voucherType === 'Sales Invoice' ? '#3b82f6' :
                                //   entry.voucherType === 'Purchase Invoice' ? '#dc2626' :
                                //   entry.voucherType === 'Receipt' ? '#059669' :
                                //   entry.voucherType === 'Payment' ? '#7c2d12' :
                                //   entry.voucherType === 'Tax Settlement' ? '#7c3aed' :
                                //   entry.voucherType === 'Opening Balance' ? '#ea580c' :
                                //   entry.voucherType === 'Journal Entry' ? '#0891b2' :
                                //   '#6b7280'
                              }}
                            />
                          </div>
                        </StyledTableCell>
                        <StyledTableCell style={{ fontSize: '9pt', fontWeight: '500', verticalAlign: 'middle' }}>
                          #{entry.voucherNumber}
                        </StyledTableCell>
                        <StyledTableCell style={{ verticalAlign: 'middle' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.3' }}>
                            <span style={{ fontWeight: '500', fontSize: '9pt' }}>
                              {entry.ledgerName}
                            </span>
                            {entry.groupCategory && (
                              <span style={{ fontSize: '8pt', color: '#6b7280', fontStyle: 'italic' }}>
                                {entry.groupCategory}
                              </span>
                            )}
                          </div>
                        </StyledTableCell>
                        <StyledTableCell style={{
                          // color: parseFloat(entry.debit) > 0 ? '#d32f2f' : '#757575',
                          textAlign: 'right',
                          fontWeight: parseFloat(entry.debit) > 0 ? '600' : 'normal',
                          verticalAlign: 'middle'
                        }}>
                          {parseFloat(entry.debit) > 0 ? Utils.formatNumber(parseFloat(entry.debit).toFixed(2)) : ''}
                        </StyledTableCell>
                        <StyledTableCell style={{
                          // color: parseFloat(entry.credit) > 0 ? '#2e7d32' : '#757575',
                          textAlign: 'right',
                          fontWeight: parseFloat(entry.credit) > 0 ? '600' : 'normal',
                          verticalAlign: 'middle'
                        }}>
                          {parseFloat(entry.credit) > 0 ? Utils.formatNumber(parseFloat(entry.credit).toFixed(2)) : ''}
                        </StyledTableCell>
                        <StyledTableCell style={{
                          // color: entry.balanceType === 'Dr' ? '#2e7d32' : '#d32f2f',
                          textAlign: 'right',
                          fontWeight: 'bold',
                          fontSize: '9pt',
                          verticalAlign: 'middle'
                        }}>
                          {Utils.formatNumber(Math.abs(parseFloat(entry.runningBalance) || 0).toFixed(2))} {entry.balanceType}
                        </StyledTableCell>
                      </StyledTableRow>
                    )) : (
                      <StyledTableRow>
                        <StyledTableCell colSpan={9} style={{ textAlign: 'center' }}>No Data Found</StyledTableCell>
                      </StyledTableRow>
                    )}
                  </TableBody>
                </Table>

                {/* Loading More Indicator */}
                {isLoadingMore && (
                  <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress size={30} color="info" />
                    <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
                      Loading more entries...
                    </Typography>
                  </Box>
                )}

                {/* Showing entries info */}
                {dayBookData && dayBookData.pagination && !isLoading && (
                  <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      Showing {allEntries.length} of {dayBookData.pagination.totalRecords} entries
                      {!hasMore && allEntries.length > 0 && ' - All entries loaded'}
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              <Grid container p={2}>
                <Grid xs={12} style={{ textAlign: 'center' }}>
                  <CircularProgress color="info" />
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Fixed Total Row */}
          {!isLoading && dayBookData?.summary && (
            <Box>
              <Table sx={{ minWidth: 700, tableLayout: 'fixed', width: '100%' }} size='small'>
                <colgroup>
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '12%' }} />
                </colgroup>
                <TableBody>
                  <TableRow style={{ backgroundColor: '#f1f5f9' }}>
                    <StyledTableCell style={{
                      fontWeight: 'bold',
                      fontSize: '11pt',
                      backgroundColor: '#f1f5f9',
                      color: '#1e293b',
                      border: '0.75px solid #d9d4d4',
                      borderTop: '2px solid #64748b',
                      padding: 5,
                      paddingLeft: '15px !important'
                    }}>
                      TOTAL
                    </StyledTableCell>
                    <StyledTableCell style={{
                      backgroundColor: '#f1f5f9',
                      border: '0.75px solid #d9d4d4',
                      borderTop: '2px solid #64748b',
                      padding: 5
                    }}>
                    </StyledTableCell>
                    <StyledTableCell style={{
                      backgroundColor: '#f1f5f9',
                      border: '0.75px solid #d9d4d4',
                      borderTop: '2px solid #64748b',
                      padding: 5
                    }}>
                    </StyledTableCell>
                    <StyledTableCell style={{
                      backgroundColor: '#f1f5f9',
                      border: '0.75px solid #d9d4d4',
                      borderTop: '2px solid #64748b',
                      padding: 5
                    }}>
                    </StyledTableCell>
                    <StyledTableCell style={{
                      backgroundColor: '#f1f5f9',
                      border: '0.75px solid #d9d4d4',
                      borderTop: '2px solid #64748b',
                      padding: 5
                    }}>
                    </StyledTableCell>
                    <StyledTableCell style={{
                      textAlign: 'right',
                      fontWeight: 'bold',
                      fontSize: '10pt',
                      color: '#dc2626',
                      backgroundColor: '#fee2e2',
                      border: '0.75px solid #d9d4d4',
                      borderTop: '2px solid #64748b',
                      borderLeft: '1px solid #dc2626',
                      borderRight: '1px solid #dc2626',
                      borderBottom: '1px solid #dc2626',
                      padding: 5,
                      paddingLeft: '15px !important',
                      paddingRight: '15px !important'
                    }}>
                      {Utils.formatNumber(parseFloat(dayBookData.summary.totalDebit || '0').toFixed(2))}
                    </StyledTableCell>
                    <StyledTableCell style={{
                      textAlign: 'right',
                      fontWeight: 'bold',
                      fontSize: '10pt',
                      color: '#059669',
                      backgroundColor: '#dcfce7',
                      border: '0.75px solid #d9d4d4',
                      borderTop: '2px solid #64748b',
                      borderLeft: '1px solid #059669',
                      borderRight: '1px solid #059669',
                      borderBottom: '1px solid #059669',
                      padding: 5,
                      paddingLeft: '15px !important',
                      paddingRight: '15px !important'
                    }}>
                      {Utils.formatNumber(parseFloat(dayBookData.summary.totalCredit || '0').toFixed(2))}
                    </StyledTableCell>
                    <StyledTableCell style={{
                      textAlign: 'right',
                      fontWeight: 'bold',
                      fontSize: '10pt',
                      color: dayBookData.summary.closingBalanceType === 'Dr' ? '#2e7d32' : '#d32f2f',
                      backgroundColor: dayBookData.summary.closingBalanceType === 'Dr' ? '#dcfce7' : '#fee2e2',
                      border: '0.75px solid #d9d4d4',
                      borderTop: '2px solid #64748b',
                      borderLeft: `1px solid ${dayBookData.summary.closingBalanceType === 'Dr' ? '#2e7d32' : '#d32f2f'}`,
                      borderRight: `1px solid ${dayBookData.summary.closingBalanceType === 'Dr' ? '#2e7d32' : '#d32f2f'}`,
                      borderBottom: `1px solid ${dayBookData.summary.closingBalanceType === 'Dr' ? '#2e7d32' : '#d32f2f'}`,
                      padding: 5,
                      paddingLeft: '15px !important',
                      paddingRight: '15px !important'
                    }}>
                      {Utils.formatNumber(Math.abs(parseFloat(dayBookData.summary.closingBalance || '0')).toFixed(2))} {dayBookData.summary.closingBalanceType}
                    </StyledTableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          )}

          {/* Scroll to Top/Bottom Button */}
          <Fade in={showScrollButton}>
            <Fab
              color="primary"
              size="medium"
              onClick={handleScrollButtonClick}
              sx={{
                position: 'absolute',
                bottom: 46,
                right: 16,
                zIndex: 1000,
                boxShadow: 3,
                '&:hover': {
                  boxShadow: 6,
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.3s ease-in-out'
              }}
              aria-label={scrollDirection === 'down' ? 'scroll to bottom' : 'scroll to top'}
            >
              {scrollDirection === 'down' ? <ArrowDownIcon /> : <ArrowUpIcon />}
            </Fab>
          </Fade>
        </Box>
      </Grid>
    </Grid>
  );
};

export default DayBookReportPage;
